import { useQuery } from '@apollo/client';
import { Search, Wallet } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OPEN_ACCOUNTS_QUERY } from './open-accounts.gql';
import { OpenAccountDetailDialog } from './OpenAccountDetailDialog';

import {
  OPEN_ACCOUNT_KIND_FROM_GQL,
  OPEN_ACCOUNT_KIND_TO_GQL,
  OPEN_ACCOUNT_STATUS_FROM_GQL,
  OPEN_ACCOUNT_STATUS_TO_GQL,
} from '@/shared/graphql/enums';
import { useBranchScope } from '@/modules/auth/useAuth';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';

type OpenAccountStatus = 'open' | 'settled' | 'transferred' | 'deceased' | 'cancelled';
type OpenAccountStatusFilter = OpenAccountStatus | 'all';

const STATUS_FILTERS: readonly OpenAccountStatusFilter[] = [
  'open',
  'settled',
  'transferred',
  'deceased',
  'cancelled',
  'all',
];

const STATUS_STYLES: Record<OpenAccountStatus, { card: string; badge: string }> = {
  open: {
    card: 'border-amber-500/40 bg-amber-500/[0.06] hover:bg-amber-500/10 dark:border-amber-400/30 dark:bg-amber-500/10',
    badge:
      'border-amber-500/50 bg-amber-500/10 text-amber-800 dark:border-amber-400/40 dark:text-amber-200',
  },
  settled: {
    card: 'border-emerald-500/40 bg-emerald-500/[0.06] hover:bg-emerald-500/10 dark:border-emerald-400/30 dark:bg-emerald-500/10',
    badge:
      'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:border-emerald-400/40 dark:text-emerald-200',
  },
  transferred: {
    card: 'border-sky-500/40 bg-sky-500/[0.06] hover:bg-sky-500/10 dark:border-sky-400/30 dark:bg-sky-500/10',
    badge: 'border-sky-500/50 bg-sky-500/10 text-sky-800 dark:border-sky-400/40 dark:text-sky-200',
  },
  deceased: {
    card: 'border-slate-400/50 bg-slate-500/[0.06] hover:bg-slate-500/10 dark:border-slate-400/30 dark:bg-slate-400/10',
    badge:
      'border-slate-400/60 bg-slate-500/10 text-slate-700 dark:border-slate-400/40 dark:text-slate-200',
  },
  cancelled: {
    card: 'border-red-500/40 bg-red-500/[0.06] hover:bg-red-500/10 dark:border-red-400/30 dark:bg-red-500/10',
    badge: 'border-red-500/50 bg-red-500/10 text-red-800 dark:border-red-400/40 dark:text-red-200',
  },
};

interface OpenAccount {
  id: string;
  folio_number: string | null;
  customer_id: string;
  pet_id: string;
  kind: string;
  status: string;
  balance_cents: string;
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
  tutor_name: string | null;
  pet_name: string | null;
}

interface AccountsResponse {
  openAccounts: { items: OpenAccount[]; page: { total: number; has_more: boolean } };
}

export function OpenAccountsPage(): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const [filter_status, setFilterStatus] = useState<OpenAccountStatusFilter>('open');
  const [filter_kind, setFilterKind] = useState<
    'normal' | 'hospitalization_1' | 'hospitalization_2' | 'all'
  >('all');
  const [search_input, setSearchInput] = useState('');
  const [applied_search, setAppliedSearch] = useState('');
  const [search_by_tutor, setSearchByTutor] = useState(false);
  const [selected_id, setSelectedId] = useState<string | null>(null);
  const { data, loading, error } = useQuery<AccountsResponse>(OPEN_ACCOUNTS_QUERY, {
    variables: {
      offset: 0,
      limit: 50,
      search: applied_search || null,
      search_by_tutor,
      kind: filter_kind === 'all' ? null : OPEN_ACCOUNT_KIND_TO_GQL[filter_kind as keyof typeof OPEN_ACCOUNT_KIND_TO_GQL],
      status: filter_status === 'all' ? null : OPEN_ACCOUNT_STATUS_TO_GQL[filter_status],
      branch_id,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const items = (data?.openAccounts.items ?? []).map((item) => ({
    ...item,
    kind:
      OPEN_ACCOUNT_KIND_FROM_GQL[item.kind as keyof typeof OPEN_ACCOUNT_KIND_FROM_GQL] ?? item.kind,
    status:
      OPEN_ACCOUNT_STATUS_FROM_GQL[item.status as keyof typeof OPEN_ACCOUNT_STATUS_FROM_GQL] ??
      item.status,
  }));

  function applySearch(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setAppliedSearch(search_input.trim().replace(/\s+/g, ' '));
  }

  function clearSearch(): void {
    setSearchInput('');
    setAppliedSearch('');
  }

  return (
    <div className="space-y-4">
      <header>
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('open_accounts.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('open_accounts.subtitle')}</p>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <form className="flex flex-col gap-2 sm:flex-row" onSubmit={applySearch}>
            <div className="relative min-w-0 flex-1">
              <label className="sr-only" htmlFor="open-accounts-search">
                {t('open_accounts.search_label')}
              </label>
              <Search
                className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                aria-hidden
              />
              <Input
                id="open-accounts-search"
                name="open-accounts-search"
                type="search"
                autoComplete="off"
                spellCheck={false}
                value={search_input}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={
                  search_by_tutor
                    ? t('open_accounts.search_placeholder_tutor')
                    : t('open_accounts.search_placeholder')
                }
                className="pl-9"
              />
            </div>
            <label className="flex shrink-0 items-center gap-2 text-sm whitespace-nowrap">
              <input
                type="checkbox"
                checked={search_by_tutor}
                onChange={(event) => setSearchByTutor(event.target.checked)}
              />
              {t('open_accounts.search_by_tutor')}
            </label>
            <Button type="submit">{t('common.search')}</Button>
            <Button type="button" variant="outline" onClick={clearSearch}>
              {t('open_accounts.clear_search')}
            </Button>
          </form>

          <div className="grid gap-3 border-t pt-4 lg:grid-cols-[auto_1fr]">
            <fieldset className="space-y-2">
              <legend className="text-muted-foreground text-xs font-medium">
                {t('open_accounts.kind_filter_label')}
              </legend>
              <div className="flex flex-wrap gap-2">
                {(['normal', 'hospitalization_1', 'hospitalization_2', 'all'] as const).map((kind) => (
                  <Button
                    key={kind}
                    type="button"
                    size="sm"
                    variant={filter_kind === kind ? 'default' : 'outline'}
                    aria-pressed={filter_kind === kind}
                    onClick={() => setFilterKind(kind)}
                  >
                    {t(`open_accounts.kind_${kind}`)}
                  </Button>
                ))}
              </div>
            </fieldset>

            <fieldset className="space-y-2 lg:border-l lg:pl-4">
              <legend className="text-muted-foreground text-xs font-medium">
                {t('open_accounts.status_filter_label')}
              </legend>
              <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((status) => (
                  <Button
                    key={status}
                    type="button"
                    size="sm"
                    variant={filter_status === status ? 'default' : 'outline'}
                    aria-pressed={filter_status === status}
                    onClick={() => setFilterStatus(status)}
                  >
                    {status === 'all'
                      ? t('open_accounts.filter_all')
                      : t(`open_accounts.status_${status}`)}
                  </Button>
                ))}
              </div>
            </fieldset>
          </div>

          {loading ? (
            <p className="text-muted-foreground text-xs" role="status" aria-live="polite">
              {t('open_accounts.loading')}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {error ? (
        <div
          className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
          role="alert"
        >
          {t('open_accounts.load_error')}
        </div>
      ) : null}

      {!error && loading && items.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm" role="status">
            {t('open_accounts.loading')}
          </CardContent>
        </Card>
      ) : !error && items.length === 0 ? (
        <Card>
          <CardContent
            className="text-muted-foreground py-6 text-center text-sm"
            role="status"
            aria-live="polite"
          >
            {applied_search ? t('open_accounts.empty_search') : t('open_accounts.empty')}
          </CardContent>
        </Card>
      ) : !error ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((acc) => {
            const status_style = STATUS_STYLES[acc.status];
            return (
              <button
                key={acc.id}
                type="button"
                onClick={() => setSelectedId(acc.id)}
                className="focus-visible:ring-ring h-full w-full rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                aria-label={t('open_accounts.open_detail', {
                  folio: acc.folio_number ?? acc.id.slice(0, 8),
                })}
              >
                <Card className={`h-full transition-colors ${status_style?.card ?? ''}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-base">
                      <span className="flex items-center gap-2">
                        <Wallet className="h-4 w-4" aria-hidden />
                        <span className="font-mono text-xs">
                          #{acc.folio_number ?? acc.id.slice(0, 8)}
                        </span>
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{t(`open_accounts.kind_${acc.kind}`)}</Badge>
                        <Badge variant="outline" className={status_style?.badge}>
                          {t(`open_accounts.status_${acc.status}`)}
                        </Badge>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold leading-tight">
                        {t('open_accounts.tutor')}:{' '}
                        <span className="text-foreground font-normal">
                          {acc.tutor_name ?? acc.customer_id}
                        </span>
                      </p>
                      <p className="text-muted-foreground text-xs leading-tight">
                        {t('open_accounts.pet')}:{' '}
                        <span className="text-foreground">{acc.pet_name ?? acc.pet_id}</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-between font-semibold">
                      <span>{t('open_accounts.balance')}</span>
                      <span className="font-mono">{formatCentsString(acc.balance_cents)}</span>
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t('open_accounts.opened_at')}: {fmtDate(acc.opened_at)}
                    </div>
                    {acc.closed_at ? (
                      <div className="text-muted-foreground text-xs">
                        {t('open_accounts.closed_at')}: {fmtDate(acc.closed_at)}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      ) : null}

      {selected_id ? (
        <OpenAccountDetailDialog account_id={selected_id} on_close={() => setSelectedId(null)} />
      ) : null}
    </div>
  );
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
